import { typedSupabase } from "../lib/typed-supabase";
import { getFileCategory } from "../types/attachment";
import type { PostgrestError } from "@supabase/supabase-js";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { logger } from "../lib/logger";
export interface TaskMetadata {
    commentCount: number;
    attachmentCount: number;
    attachmentsByType: {
        images: number;
        documents: number;
        videos: number;
        audio: number;
        other: number;
    };
    attachmentDetails: {
        type: "images" | "documents" | "videos" | "audio" | "other";
        count: number;
        label: string;
    }[];
}
export interface TaskAttachmentSummary {
    task_id: string;
    file_type: string;
    count: number;
}
// Convert single form to plural for consistency with existing code
function normalizeCategory(category: "image" | "document" | "video" | "audio" | "other"): "images" | "documents" | "videos" | "audio" | "other" {
    const mapping = {
        image: "images" as const,
        document: "documents" as const,
        video: "videos" as const,
        audio: "audio" as const,
        other: "other" as const,
    };
    return mapping[category];
}
function categorizeFileType(mimeType: string): "images" | "documents" | "videos" | "audio" | "other" {
    const category = getFileCategory(mimeType);
    return normalizeCategory(category);
}
function formatAttachmentLabel(type: string, count: number): string {
    const labels = {
        images: count === 1 ? "image" : "images",
        documents: count === 1 ? "document" : "documents",
        videos: count === 1 ? "video" : "videos",
        audio: count === 1 ? "audio file" : "audio files",
        other: count === 1 ? "file" : "files",
    };
    return labels[type as keyof typeof labels] || "files";
}
// ============================================================================
// TASK METADATA SERVICE - Phase 4 Architecture
// ============================================================================
export interface ITaskMetadataService extends IService {
    getTaskMetadata(taskId: string): Promise<ServiceResponse<TaskMetadata>>;
    getBulkTaskMetadata(taskIds: string[]): Promise<ServiceResponse<Record<string, TaskMetadata>>>;
}
export class TaskMetadataService implements ITaskMetadataService {
    readonly name = "TaskMetadataService";
    async initialize(): Promise<void> {
        logger.info("TaskMetadataService initialized", { service: this.name });
    }
    async dispose(): Promise<void> {
        logger.info("TaskMetadataService disposed", { service: this.name });
    }
    /**
     * Get comprehensive metadata for a task including comments and attachments
     */
    async getTaskMetadata(taskId: string): Promise<ServiceResponse<TaskMetadata>> {
        const startTime = Date.now();
        try {
            // Fetch comment count
            const { count: commentCount, error: commentError } = await typedSupabase
                .from("task_comments")
                .select("*", { count: "exact", head: true })
                .eq("task_id", taskId);
            if (commentError) {
                // Suppress AbortError logs - this is expected behavior during component unmounting/organization switching
                if (commentError instanceof Error && commentError.name === 'AbortError') {
                    // Silent handling
                }
                else if (typeof commentError === 'object' && commentError !== null && 'message' in commentError &&
                    typeof commentError.message === 'string' && commentError.message.includes('AbortError')) {
                    // Silent handling for Supabase error objects
                }
                else {
                    logger.error("Error fetching comment count", { error: commentError, taskId });
                }
            }
            // Fetch attachment count and details
            const { count: attachmentCount, error: attachmentCountError } = await typedSupabase
                .from("task_attachments")
                .select("*", { count: "exact", head: true })
                .eq("task_id", taskId);
            if (attachmentCountError) {
                // Suppress AbortError logs - this is expected behavior during component unmounting/organization switching
                if (attachmentCountError instanceof Error && attachmentCountError.name === 'AbortError') {
                    // Silent handling
                }
                else if (typeof attachmentCountError === 'object' && attachmentCountError !== null && 'message' in attachmentCountError &&
                    typeof attachmentCountError.message === 'string' && attachmentCountError.message.includes('AbortError')) {
                    // Silent handling for Supabase error objects
                }
                else {
                    logger.error("Error fetching attachment count", { error: attachmentCountError, taskId });
                }
            }
            // Get detailed attachment information for categorization
            const { data: attachments, error: attachmentError } = await typedSupabase
                .from("task_attachments")
                .select("file_name, file_type, file_size")
                .eq("task_id", taskId);
            if (attachmentError) {
                // Suppress AbortError logs - this is expected behavior during component unmounting/organization switching
                if (attachmentError instanceof Error && attachmentError.name === 'AbortError') {
                    // Silent handling
                }
                else if (typeof attachmentError === 'object' && attachmentError !== null && 'message' in attachmentError &&
                    typeof attachmentError.message === 'string' && attachmentError.message.includes('AbortError')) {
                    // Silent handling for Supabase error objects
                }
                else {
                    logger.error("Error fetching attachment details", { error: attachmentError, taskId });
                }
            }
            // Categorize attachments
            const attachmentsByType = {
                images: 0,
                documents: 0,
                videos: 0,
                audio: 0,
                other: 0,
            };
            const attachmentsData = attachments || [];
            attachmentsData.forEach((attachment) => {
                const category = categorizeFileType(attachment.file_type);
                attachmentsByType[category]++;
            });
            // Create attachment details array for display
            const attachmentDetails = Object.entries(attachmentsByType)
                .filter(([_, count]) => count > 0)
                .map(([type, count]) => ({
                type: type as "images" | "documents" | "videos" | "audio" | "other",
                count,
                label: formatAttachmentLabel(type, count),
            }));
            const metadata: TaskMetadata = {
                commentCount: commentCount || 0,
                attachmentCount: attachmentsData.length,
                attachmentsByType,
                attachmentDetails,
            };
            return { success: true, data: metadata };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            // Return empty metadata on error instead of throwing
            const emptyMetadata: TaskMetadata = {
                commentCount: 0,
                attachmentCount: 0,
                attachmentsByType: {
                    images: 0,
                    documents: 0,
                    videos: 0,
                    audio: 0,
                    other: 0,
                },
                attachmentDetails: [],
            };
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Get metadata for multiple tasks efficiently
     */
    async getBulkTaskMetadata(taskIds: string[]): Promise<ServiceResponse<Record<string, TaskMetadata>>> {
        const startTime = Date.now();
        if (taskIds.length === 0) {
            return { success: true, data: {} };
        }
        // --- NEW: helper to batch large "IN" queries to avoid very long URLs ---
        const CHUNK_SIZE = 50; // safe chunk size – adjust if needed
        const chunkArray = <T>(arr: T[]): T[][] => {
            const chunks: T[][] = [];
            for (let i = 0; i < arr.length; i += CHUNK_SIZE) {
                chunks.push(arr.slice(i, i + CHUNK_SIZE));
            }
            return chunks;
        };
        const aggregateErrors = (errors: PostgrestError[]): void => {
            // Filter out AbortErrors - expected during component unmounting/navigation
            const realErrors = errors.filter(err => {
                if (typeof err === 'object' && err !== null && 'message' in err &&
                    typeof err.message === 'string' && err.message.includes('AbortError')) {
                    return false;
                }
                return true;
            });
            if (realErrors.length > 0) {
                logger.error("Aggregated errors from batch queries", { errors: realErrors });
            }
        };
        try {
            /* -------------------- Fetch comments in batches -------------------- */
            let commentData: {
                task_id: string;
            }[] = [];
            const commentErrors: PostgrestError[] = [];
            for (const chunk of chunkArray(taskIds)) {
                const { data, error } = await typedSupabase
                    .from("task_comments")
                    .select("task_id")
                    .in("task_id", chunk);
                if (error) {
                    commentErrors.push(error);
                }
                if (data) {
                    commentData = commentData.concat(data as {
                        task_id: string;
                    }[]);
                }
            }
            aggregateErrors(commentErrors);
            /* ------------------ Fetch attachments in batches ------------------ */
            let attachmentData: {
                task_id: string;
                file_type: string;
            }[] = [];
            const attachmentErrors: PostgrestError[] = [];
            for (const chunk of chunkArray(taskIds)) {
                const { data, error } = await typedSupabase
                    .from("task_attachments")
                    .select("task_id, file_type")
                    .in("task_id", chunk);
                if (error) {
                    attachmentErrors.push(error);
                }
                if (data) {
                    attachmentData = attachmentData.concat(data as {
                        task_id: string;
                        file_type: string;
                    }[]);
                }
            }
            aggregateErrors(attachmentErrors);
            // ----------------------- Process the data -----------------------
            const result: Record<string, TaskMetadata> = {};
            // Initialize with empty metadata
            taskIds.forEach((taskId) => {
                result[taskId] = {
                    commentCount: 0,
                    attachmentCount: 0,
                    attachmentsByType: {
                        images: 0,
                        documents: 0,
                        videos: 0,
                        audio: 0,
                        other: 0,
                    },
                    attachmentDetails: [],
                };
            });
            // Count comments per task
            commentData.forEach((comment) => {
                if (result[comment.task_id]) {
                    result[comment.task_id].commentCount++;
                }
            });
            // Process attachments per task
            const taskAttachments: Record<string, {
                file_type: string;
            }[]> = {};
            attachmentData.forEach((att) => {
                if (!taskAttachments[att.task_id])
                    taskAttachments[att.task_id] = [];
                taskAttachments[att.task_id].push({ file_type: att.file_type });
            });
            // Categorize attachments
            Object.entries(taskAttachments).forEach(([taskId, list]) => {
                if (!result[taskId])
                    return;
                const attachmentsByType = {
                    images: 0,
                    documents: 0,
                    videos: 0,
                    audio: 0,
                    other: 0,
                };
                list.forEach((a) => {
                    const category = categorizeFileType(a.file_type);
                    attachmentsByType[category]++;
                });
                result[taskId].attachmentCount = list.length;
                result[taskId].attachmentsByType = attachmentsByType;
                result[taskId].attachmentDetails = Object.entries(attachmentsByType)
                    .filter(([_, count]) => count > 0)
                    .map(([type, count]) => ({
                    type: type as "images" | "documents" | "videos" | "audio" | "other",
                    count,
                    label: formatAttachmentLabel(type, count),
                }));
            });
            return { success: true, data: result };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Error fetching bulk task metadata", { error: serviceError, taskIds });
            return { success: false, error: serviceError.message, code: "BULK_FETCH_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const taskMetadataService = new TaskMetadataService();
